import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CoreServicesModule } from 'ngx-core-services';
import { APP_INITIALIZER, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { HttpBackend, HttpClientModule } from '@angular/common/http';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { IonicModule } from '@ionic/angular';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore,getFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideRemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BlocksStateModule } from 'ngx-zcs-blocks';
import AWSAppSyncClient from 'aws-appsync';
import { ToastrModule } from 'ngx-toastr';
import { ServiceWorkerModule } from '@angular/service-worker';
import localeIt from '@angular/common/locales/it';
import locateEs  from '@angular/common/locales/es';
import locateDe  from '@angular/common/locales/de';
import locateFr  from '@angular/common/locales/fr';
import locatePl  from '@angular/common/locales/pl';
import locateEn  from '@angular/common/locales/en';
import * as Sentry from '@sentry/angular';

import { registerLocaleData } from '@angular/common';


import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CustomAuthModule } from './custom-auth/components/custom-auth.module';
import { CachedHttpLoader } from './cached-http-loader';

registerLocaleData(localeIt, 'it');
registerLocaleData(locateEs, 'es');
registerLocaleData(locateDe, 'de');
registerLocaleData(locateFr, 'fr');
registerLocaleData(locatePl, 'pl');
registerLocaleData(locateEn, 'en');

const HttpLoaderFactory = (http: HttpBackend) => new CachedHttpLoader(http, './assets/i18n/', '.json');
const translateConfig = {
  defaultLanguage: environment.defaultLang,
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpBackend],
  },
};

const awsClientGenerator = (opt: any) => new AWSAppSyncClient({ ...opt });

const FirebaseModules = [
  provideAuth(() => getAuth()),
  provideFirebaseApp(() => initializeApp(environment.firebase)),
  provideFirestore(() => {
    const firestore = getFirestore();
    if ( environment.emulator ) {
      connectFirestoreEmulator(firestore, environment.FIRESTORE_EMULATOR_HOST, Number(environment.FIRESTORE_EMULATOR_PORT));
    }
    return firestore;
  }),
  provideStorage(() => getStorage()),
  provideRemoteConfig(() => getRemoteConfig()),
];

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    ...FirebaseModules,
    AppRoutingModule,
    BlocksStateModule,
    BrowserAnimationsModule,
    BrowserModule,
    CustomAuthModule,
    CoreServicesModule.forRoot({
      config: {
        production: environment.production,
        firebaseConfig: environment.firebase,
        weather: environment.weather,
        timezone: environment.timezone,
        aws: { ...environment.aws, clientGen: awsClientGenerator },
        systemsTypes: ['firebase','aws'],
      },
    }),
    IonicModule.forRoot(),
    HttpClientModule,
    TranslateModule.forRoot(translateConfig),

    MatDialogModule,
    ToastrModule.forRoot(),
    ServiceWorkerModule.register('custom-service-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
